import { useTranslate } from '@tolgee/react';
import { cl } from 'lincd/utils/ClassNames';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useAppConfig } from '../../../hooks/useAppConfig.js';
import { useTorchBannerDismissed } from '../../../hooks/useTorchBannerDismissed.js';
import { TorchOverlay } from './TorchOverlay.js';
import { TorchSvg } from './TorchSvg.js';
import style from './TorchBanner.module.css';

export interface TorchBannerProps {
  /**
   * Render variant for pre-auth contexts (Signin page). Reserved for future
   * copy variations; currently has no visual effect.
   */
  isPreAuth?: boolean;
  /**
   * Offset the desktop banner to clear the fixed MainMenu sidebar.
   */
  offsetForMainMenu?: boolean;
}

/**
 * Static dismissible teaser shown at the top of PG Main and Signin.
 *
 * Visibility is gated by:
 *   - client-mounted flag (avoids SSR/client hydration mismatch — useAppConfig's
 *     module-level cache can differ between server and client runtimes)
 *   - admin global flag `AppConfig.showTorchBanner` (via useAppConfig)
 *   - per-session dismiss (via useTorchBannerDismissed)
 *
 * Tapping the banner currently opens a TEMPORARY placeholder modal — Phase 4
 * replaces that with the real <TorchOverlay />.
 */
export function TorchBanner({
  isPreAuth,
  offsetForMainMenu,
}: TorchBannerProps) {
  const { t } = useTranslate();

  // Render nothing until after client mount. Guarantees the first render on
  // both server and client is identical (null), preventing hydration errors
  // from cache divergence in useAppConfig or sessionStorage reads in
  // useTorchBannerDismissed.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { showTorchBanner, loading } = useAppConfig();
  const { dismissed, dismiss } = useTorchBannerDismissed();
  const [modalOpen, setModalOpen] = useState(false);

  const visible = mounted && !loading && showTorchBanner && !dismissed;

  // Publish the banner's effective height as a CSS variable so host page
  // layouts (e.g. ActionsOverview's absolutely-positioned action container
  // on desktop) can offset their content to clear the banner. Set to 0px
  // when not visible so layouts collapse back to their original positions.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    if (!visible) {
      root.style.removeProperty('--torch-banner-height');
      return;
    }

    root.style.setProperty('--torch-banner-height', '104px');

    return () => {
      root.style.removeProperty('--torch-banner-height');
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  const handleBannerClick = () => setModalOpen(true);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismiss();
  };

  return (
    <>
      <button
        type="button"
        className={cl(
          style.banner,
          offsetForMainMenu && style.withMainMenuOffset
        )}
        onClick={handleBannerClick}
        aria-label={t('peaceFlame.bannerCta', 'Light the Torch of Peace')}
      >
        <TorchSvg
          className={style.torchIcon}
          pulse
          pulseClassName={style.flamePulse}
        />
        <span className={style.label}>
          {t('peaceFlame.bannerCta', 'Light the Torch of Peace')}
        </span>
        <span
          className={style.dismiss}
          role="button"
          tabIndex={0}
          aria-label={t(
            'peaceFlame.bannerDismissAria',
            'Dismiss Torch of Peace banner'
          )}
          onClick={handleDismiss}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              dismiss();
            }
          }}
        >
          ×
        </span>
      </button>

      {modalOpen && (
        <TorchOverlay
          onClose={() => setModalOpen(false)}
          isPreAuth={isPreAuth}
        />
      )}
    </>
  );
}
